<script setup lang="ts">
  import { computed, inject, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import {
    Form,
    Input,
    InputNumber,
    Select,
    SelectOption,
    Checkbox,
    Button,
    AutoComplete
  } from 'ant-design-vue'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { Calculation } from '../models/Calculation'
  import { CalculationSchema } from '../models/CalculationSchema'
  import { CalculationFieldType } from '../models/CalculationFieldType'
  import { debounceTime, groupBy, map, mergeMap, Subject } from 'rxjs'
  import { parseSmartValue, DEFAULT_INPUT_DEBOUNCE_MS } from '@/utils'
  import { CalculationsService } from '../services/CalculationsService'
  import { MinusSquare, PlusSquare } from 'lucide-vue-next'
  import {
    AppstoreOutlined,
    BorderOutlined,
    CompressOutlined,
    ExpandOutlined
  } from '@ant-design/icons-vue'
  import ArrayValuesCalculationConfiguration from './ArrayValuesCalculationConfiguration.vue'
  import JsonCalculationConfiguration from './JsonCalculationConfiguration.vue'
  import SelectOrCreateCalculationConfiguration from './SelectOrCreateCalculationConfiguration.vue'
  import { CalculationField } from '../models/CalculationField'
  import ColorCalculationConfiguration from './ColorCalculationConfiguration.vue'
  import ColumnMultiSelectOrCreateCalculationConfiguration from './ColumnMultiSelectOrCreateCalculationConfiguration.vue'
  import { CalculationSchemaUiFieldGroup } from '../models/CalculationSchemaUi'
  import { CalculationsJsonCalculationConfigurationService } from '../services/CalculationJsonCalculationConfigurationService'
  import ArrayCalculationBuilder from './ArrayCalculationBuilder.vue'
  import ObjectCalculationBuilder from './ObjectCalculationBuilder.vue'
  import RegexCalculationConfiguration from './RegexCalculationConfiguration.vue'
  import CalculationHelpPopover from '../help/components/CalculationHelpPopover.vue'
  import StringTemplateBuilder from '@/shared/StringTemplateBuilder/components/StringTemplateBuilder.vue'
  import { CalculationsUiService } from '../services/CalculationsUiService'

  interface ICalculationConfigurationProps {
    calculationSchemas: Array<CalculationSchema>
    calculation: Calculation | null
    uiFieldGroup?: CalculationSchemaUiFieldGroup
    availableColumns: Array<{ value: string; label: string }>
    availableParams: Array<{ value: string; label: string }>
    configurationLevel: number
    showDescription?: boolean
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
    currentPath?: string[]
  }

  interface ICalculationConfigurationEmits {
    (
      e: 'configuration-updated',
      calculation: Calculation,
      config: Record<string, any>,
      calculationTypeChange?: boolean
    ): void
  }

  const props = defineProps<ICalculationConfigurationProps>()
  const emit = defineEmits<ICalculationConfigurationEmits>()

  const configuration = ref<Record<string, any>>({})
  const isCollapsed = ref<boolean>(false)
  const isCondensedLayout = ref<boolean>(props.isCondensedLayout)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })
  const { controlSize } = useWindowSize()

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const calculationDescription = ref(props.calculation?.configuration?.description ?? '')

  watch(calculationDescription, (newVal) => {
    if (configuration.value) {
      configuration.value.description = newVal
      handleConfigurationChange()
    }
  })

  const inputValueChanged$ = new Subject<{ field: string; value: string }>()

  const inputValueChangedSubscription = inputValueChanged$
    .pipe(
      groupBy(({ field }) => field),
      mergeMap((group$) =>
        group$.pipe(
          debounceTime(DEFAULT_INPUT_DEBOUNCE_MS),
          map(({ field, value }) => ({ field, value }))
        )
      )
    )
    .subscribe(({ field, value }) => {
      const parsedValue = parseSmartValue(value)
      configuration.value[field] = parsedValue
      handleConfigurationChange()
    })

  // Initialize configuration with default values
  const initializeConfiguration = () => {
    if (!props.calculation) return
    configuration.value = CalculationsService.initializeCalculationConfiguration(
      props.calculation,
      props.calculation.schema.fields
    )
  }

  const handleConfigurationChange = () => {
    if (props.calculation) {
      emit('configuration-updated', props.calculation, configuration.value)
    }
  }

  const handleChildCalculationTypeChanged = (
    calculationType: string,
    childCalculation: Calculation,
    configuration: any
  ) => {
    if (childCalculation) {
      emit(
        'configuration-updated',
        { ...childCalculation, _type: calculationType },
        configuration,
        true
      )
    }
  }

  const handleCalculationTypeChanged = (calculationType: string, configuration: any) => {
    if (props.calculation) {
      emit(
        'configuration-updated',
        { ...props.calculation, _type: calculationType },
        configuration?.value,
        true
      )
    }
  }

  watch(
    () => props.calculation,
    (newCalculation) => {
      if (newCalculation) {
        configuration.value = { ...newCalculation.configuration }
        // initialize with defaults
        if (Object.keys(configuration.value).length === 0) {
          initializeConfiguration()
          handleConfigurationChange()
        }
        calculationDescription.value = configuration.value.description ?? ''
      }
    },
    { immediate: true, deep: true }
  )

  const pendingRefocus = inject(pendingRefocusKey)

  let isUnmounting = false

  const onInputChange = (newValue: string, fieldName: string) => {
    const activeEl = document.activeElement as HTMLInputElement | null
    if (pendingRefocus) {
      pendingRefocus.value = {
        levelFieldKey: `${props.configurationLevel}:${fieldName}`,
        cursor: activeEl?.selectionStart ?? null
      }
    }
    inputValueChanged$.next({ value: newValue, field: fieldName })
  }

  const onInputBlur = (fieldKey: string) => {
    if (isUnmounting) return
    if (pendingRefocus?.value?.levelFieldKey === `${props.configurationLevel}:${fieldKey}`) {
      pendingRefocus.value = null
    }
  }

  const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value
  }

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }

  const onLayoutModeChanged = (e: any) => {
    isCondensedLayout.value = e.target.value
  }

  // If only single field, do not use grid css
  const simpleFieldCount = computed(() =>
    props.calculation
      ? props.calculation.schema.fields.filter(
          (f: any) =>
            f.field_type !== CalculationFieldType.Object &&
            f.field_type !== CalculationFieldType.Array &&
            f.field_type !== CalculationFieldType.Json
        ).length
      : 0
  )

  const configuredFields = computed(() => {
    const calculation = props.calculation
    const uiFieldGroup = props.uiFieldGroup

    if (!calculation) {
      return []
    }

    const allFields = calculation.schema.fields

    if (!uiFieldGroup || !Array.isArray(uiFieldGroup.fields) || uiFieldGroup.fields.length === 0) {
      return allFields
    }

    // Ensure order matches uiFieldGroup.fields
    return uiFieldGroup.fields
      .map((fieldName) => allFields.find((field: CalculationField) => field.name === fieldName))
      .filter((field): field is CalculationField => !!field)
  })

  const shouldShowGlobalCollapseButton = computed(() => {
    return (
      props.calculation?.schema?.fields?.findIndex(
        (x: CalculationField) =>
          x.field_type === CalculationFieldType.Array ||
          x.field_type === CalculationFieldType.Object ||
          x.field_type === CalculationFieldType.Json
      ) !== -1
    )
  })

  const shouldShowFieldInformation = (field: CalculationField): boolean => {
    return (
      field.field_type !== CalculationFieldType.Object &&
      field.field_type !== CalculationFieldType.Json
    )
  }

  const checkboxSizeClass = computed(() => {
    if (controlSize.value === 'small') return 'checkbox-sm'
    if (controlSize.value === 'large') return 'checkbox-lg'
    return 'checkbox-md'
  })

  const displayField = computed(() => {
    const mappedField = props.calculation?.display_field as
      | string
      | Array<string>
      | null
      | undefined
    if (mappedField == null) return undefined

    const fields = props.calculation?.schema?.fields ?? []

    const valueForName = (name: string) => {
      const matchingField = fields.find((x: any) => x.name === name)
      if (!matchingField) return undefined
      const val = configuration.value?.[matchingField.name]
      return val === undefined ? undefined : val
    }

    if (typeof mappedField === 'string') {
      return valueForName(mappedField)
    }

    if (Array.isArray(mappedField)) {
      for (const name of mappedField) {
        const v = valueForName(name)
        if (v) return v
      }
    }

    return undefined
  })

  onMounted(() => {
    if (pendingRefocus?.value) {
      const { levelFieldKey, cursor } = pendingRefocus.value
      let el = document.querySelector(`[data-field="${levelFieldKey}"]`) as HTMLElement | null
      if (el && el.tagName !== 'INPUT') el = el.querySelector('input') ?? el
      const inputEl = el as HTMLInputElement | null
      if (inputEl?.tagName === 'INPUT') {
        inputEl.focus()
        if (cursor !== null) {
          try { inputEl.setSelectionRange(cursor, cursor) } catch {}
        }
      }
      pendingRefocus.value = null
    }
  })

  onBeforeUnmount(() => {
    isUnmounting = true
    inputValueChangedSubscription?.unsubscribe()
  })
</script>

<template>
  <div
    v-if="calculation"
    :class="[
      'border',
      'calculation-configuration',
      'h-full',
      'w-full',
      {
        '!bg-[var(--card)] dark:!bg-[var(--card-light)]': props.configurationLevel % 2 !== 0,
        '!bg-[var(--card-light)] dark:!bg-[var(--card)]': props.configurationLevel % 2 === 0,
        'child-calculation-configuration border p-2': props.configurationLevel > 1
      }
    ]"
  >
    <div class="flex h-full flex-col">
      <div v-if="props.configurationLevel <= 1" class="mb-2 flex items-center justify-end gap-2">
        <a-tooltip>
          <template #title>Toggle layout mode</template>
          <a-radio-group
            :value="isCondensedLayout"
            @change="onLayoutModeChanged"
            :size="controlSize"
          >
            <a-radio-button :value="true">
              <AppstoreOutlined class="radio-icon-center" />
            </a-radio-button>
            <a-radio-button :value="false">
              <BorderOutlined class="radio-icon-center" />
            </a-radio-button>
          </a-radio-group>
        </a-tooltip>
      </div>
      <div class="horizontal-rule mb-4 border-b pb-1" v-if="props.configurationLevel > 1">
        <div class="flex">
          <div>
            <h3 class="mt-1 text-lg font-semibold">
              Configure: {{ calculation.display_name }}
              <span v-if="displayField != null">{{ `(${displayField})` }}</span>
            </h3>
          </div>
          <div class="ml-auto flex">
            <a-tooltip placement="top">
              <template #title>Toggle Collapse</template>
              <Button
                style="padding: 3px; border: none"
                type="text"
                :size="controlSize"
                class="flex items-center justify-center"
                @click="toggleCollapse"
                aria-label="Toggle Collapse"
              >
                <PlusSquare
                  v-if="isCollapsed"
                  class="text-base"
                  style="line-height: 1; margin: 0"
                />
                <MinusSquare v-else class="text-base" style="line-height: 1; margin: 0" />
              </Button>
            </a-tooltip>

            <a-tooltip v-if="shouldShowGlobalCollapseButton" placement="top">
              <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
              <Button
                v-if="shouldShowGlobalCollapseButton"
                style="padding: 3px; border: none"
                type="text"
                :size="controlSize"
                class="flex items-center justify-center"
                @click="toggleGlobalCollapse"
                aria-label="Toggle Collapse All"
              >
                <CompressOutlined
                  v-if="isCollapsed"
                  class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                  style="line-height: 1; margin: 0"
                />
                <ExpandOutlined
                  v-else
                  class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                  style="line-height: 1; margin: 0"
                />
              </Button>
            </a-tooltip>
          </div>
        </div>
      </div>
      <div
        v-else-if="
          calculation._type !== CalculationsService.CUSTOM_CALCULATION_TYPE &&
          props.showDescription === true
        "
        class="mb-4"
      >
        <label class="mb-1 block text-sm font-medium">Description</label>
        <Input
          v-model:value="calculationDescription"
          @change="handleConfigurationChange"
          placeholder="Calculation description"
          :size="controlSize"
        />
      </div>

      <div
        :class="[
          'flex-1',
          'overflow-y-auto',
          { 'collapsed-calculation-configuration': isCollapsed }
        ]"
      >
        <Form ref="formRef" :model="configuration" layout="vertical">
          <div
            :class="{
              'calculation-form-fields-grid': true,
              expanded:
                props.configurationLevel === 1 ? !isCondensedLayout : !props.isCondensedLayout
            }"
          >
            <div
              v-for="configuredField in configuredFields"
              :key="configuredField.name"
              class="calculation-form-field-grid"
              :class="{
                'array-calculation-builder-grid-span':
                  configuredField.field_type === CalculationFieldType.Array,
                'object-calculation-builder-grid-span':
                  configuredField.field_type === CalculationFieldType.Object ||
                  configuredField.field_type === CalculationFieldType.StringTemplate,
                'json-calculation-configuration-grid-span':
                  configuredField.field_type === CalculationFieldType.Json,
                'single-field-grid-span':
                  simpleFieldCount <= 1 &&
                  configuredField.field_type !== CalculationFieldType.Object &&
                  configuredField.field_type !== CalculationFieldType.Array &&
                  configuredField.field_type !== CalculationFieldType.Json,
                '!hidden': !CalculationsUiService.isFieldVisibleFromRoot(
                  configuredField,
                  props.calculation,
                  props.currentPath
                    ? [...props.currentPath, configuredField.name]
                    : [configuredField.name]
                )
              }"
            >
              <div class="flex">
                <div v-if="shouldShowFieldInformation(configuredField)" class="min-w-0 flex-1">
                  <label class="calculation-field-label mb-1 block text-sm font-medium">
                    {{ configuredField.display_name }}
                    <span v-if="configuredField.required" class="text-red-500">*</span>
                  </label>
                  <p
                    v-if="configuredField.description"
                    class="calculation-field-label mb-2 text-xs"
                  >
                    {{ configuredField.description }}
                  </p>
                </div>
                <div
                  v-if="
                    configuredField.help_content != null && configuredField.help_content?.length > 0
                  "
                  class="m1-auto flex justify-end"
                >
                  <CalculationHelpPopover
                    :content-type="configuredField.help_content"
                    :title="`${configuredField.display_name} Help`"
                  />
                </div>
              </div>

              <!-- Text Input -->
              <Input
                v-if="configuredField.field_type === CalculationFieldType.Text"
                :value="configuration[configuredField.name]"
                @update:value="(val: string) => onInputChange(val, configuredField.name)"
                @blur="() => onInputBlur(configuredField.name)"
                :data-field="`${props.configurationLevel}:${configuredField.name}`"
                :placeholder="configuredField.placeholder || ''"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                :size="controlSize"
              />

              <!-- Number Input -->
              <InputNumber
                v-else-if="configuredField.field_type === CalculationFieldType.Number"
                v-model:value="configuration[configuredField.name]"
                :placeholder="configuredField.placeholder || ''"
                :size="controlSize"
                @change="handleConfigurationChange"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                class="w-full"
              />

              <!-- Regex Input -->
              <RegexCalculationConfiguration
                v-else-if="configuredField.field_type === CalculationFieldType.RegularExpression"
                v-model="configuration[configuredField.name]"
                :required="configuredField.required"
                :regex="configuredField.field_type_args?.regex_pattern"
                :data-type="configuredField.field_type_args?.regex_data_type"
                :placeholder="configuredField.placeholder || ''"
                :error-message="configuredField.field_type_args?.regex_description"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Select Dropdown -->
              <Select
                v-else-if="configuredField.field_type === CalculationFieldType.Select"
                v-model:value="configuration[configuredField.name]"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="option in configuredField.options"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>
              </Select>

              <!-- Select Dropdown JSON Payload -->
              <Select
                v-else-if="configuredField.field_type === CalculationFieldType.SelectJson"
                :value="JSON.stringify(configuration[configuredField.name])"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @update:value="
                  (val: string) => {
                    try {
                      const parsed = JSON.parse(val)
                      configuration[configuredField.name] = parsed
                      handleConfigurationChange()
                    } catch (e) {
                      console.error('Failed to parse selected JSON value', e)
                    }
                  }
                "
                :size="controlSize"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="option in configuredField.options"
                  :key="JSON.stringify(option.value)"
                  :value="JSON.stringify(option.value)"
                >
                  <span v-html="option.label"></span>
                </SelectOption>
              </Select>

              <!-- Select Dropdown Or Create -->
              <SelectOrCreateCalculationConfiguration
                v-else-if="configuredField.field_type === CalculationFieldType.SelectOrCreate"
                v-model="configuration[configuredField.name]"
                :placeholder="`Select or enter new ${configuredField.display_name.toLowerCase()}`"
                :options="configuredField.options"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Multi-Select -->
              <Select
                v-else-if="
                  configuredField.field_type === CalculationFieldType.MultiSelect ||
                  configuredField.field_type === CalculationFieldType.ColumnMultiSelect ||
                  configuredField.field_type === CalculationFieldType.ParamMultiSelect
                "
                v-model:value="configuration[configuredField.name]"
                mode="multiple"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-if="configuredField.field_type === CalculationFieldType.MultiSelect"
                  v-for="option in configuredField.options"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>

                <SelectOption
                  v-if="configuredField.field_type === CalculationFieldType.ColumnMultiSelect"
                  v-for="option in props.availableColumns"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>

                <SelectOption
                  v-if="configuredField.field_type === CalculationFieldType.ParamMultiSelect"
                  v-for="option in props.availableParams"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>
              </Select>

              <!-- Boolean Checkbox size -->
              <!-- Boolean Checkbox -->
              <Checkbox
                v-else-if="configuredField.field_type === CalculationFieldType.Boolean"
                @change="handleConfigurationChange"
                v-model:checked="configuration[configuredField.name]"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                :class="checkboxSizeClass"
              >
                {{ configuredField.display_name }}
              </Checkbox>

              <!-- Column Select -->
              <Select
                v-else-if="configuredField.field_type === CalculationFieldType.ColumnSelect"
                v-model:value="configuration[configuredField.name]"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="column in props.availableColumns"
                  :key="column.value"
                  :value="column.value"
                >
                  {{ column.label }}
                </SelectOption>
              </Select>

              <!-- Param Select -->
              <Select
                v-else-if="configuredField.field_type === CalculationFieldType.ParamSelect"
                v-model:value="configuration[configuredField.name]"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="column in props.availableParams"
                  :key="column.value"
                  :value="column.value"
                >
                  {{ column.label }}
                </SelectOption>
              </Select>

              <!-- Column Select or Create -->
              <AutoComplete
                v-else-if="configuredField.field_type === CalculationFieldType.ColumnSelectOrCreate"
                v-model:value="configuration[configuredField.name]"
                :options="props.availableColumns"
                :placeholder="
                  configuredField.placeholder || `Select existing column or enter new column name`
                "
                :size="controlSize"
                class="w-full"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                @change="handleConfigurationChange"
                allow-clear
                showSearch
              />

              <!-- Param Select or Create -->
              <AutoComplete
                v-else-if="configuredField.field_type === CalculationFieldType.ParamSelectOrCreate"
                v-model:value="configuration[configuredField.name]"
                :options="props.availableParams"
                :placeholder="
                  configuredField.placeholder || `Select existing param or enter new param`
                "
                :size="controlSize"
                class="w-full"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                @change="handleConfigurationChange"
                showSearch
                allowClear
              />

              <!-- Multi Column Select Or Create -->
              <ColumnMultiSelectOrCreateCalculationConfiguration
                v-else-if="
                  configuredField.field_type === CalculationFieldType.ColumnMultiSelectOrCreate
                "
                v-model="configuration[configuredField.name]"
                :placeholder="`Select or enter new ${configuredField.display_name.toLowerCase()} values`"
                :available-columns="props.availableColumns"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Param Column Select Or Create -->
              <ColumnMultiSelectOrCreateCalculationConfiguration
                v-else-if="
                  configuredField.field_type === CalculationFieldType.ParamMultiSelectOrCreate
                "
                v-model="configuration[configuredField.name]"
                :placeholder="`Select or enter new ${configuredField.display_name.toLowerCase()} values`"
                :available-columns="props.availableParams"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Array Values -->
              <ArrayValuesCalculationConfiguration
                v-else-if="configuredField.field_type === CalculationFieldType.ArrayValues"
                v-model="configuration[configuredField.name]"
                :place-holder="configuredField.placeholder || 'Enter value(s)'"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Color Picker -->
              <ColorCalculationConfiguration
                v-else-if="configuredField.field_type === CalculationFieldType.ColorPicker"
                v-model="configuration[configuredField.name]"
                :place-holder="configuredField.placeholder || 'Select color'"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Array Type -->
              <ArrayCalculationBuilder
                v-else-if="configuredField.field_type === CalculationFieldType.Array"
                :configuration-level="props.configurationLevel"
                :available-columns="props.availableColumns"
                :available-params="props.availableParams"
                v-model="configuration[configuredField.name]"
                :calculation-field="configuredField"
                :key="calculation.id"
                @update:modelValue="handleConfigurationChange"
                @child-calculation-type-changed="handleChildCalculationTypeChanged"
                :calculation-schemas="calculationSchemas"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
              />

              <!-- Object Type -->
              <ObjectCalculationBuilder
                v-else-if="configuredField.field_type === CalculationFieldType.Object"
                :configuration-level="props.configurationLevel + 1"
                :available-columns="props.availableColumns"
                :available-params="props.availableParams"
                v-model="configuration[configuredField.name]"
                :calculation-field="configuredField"
                @update:modelValue="handleConfigurationChange"
                :calculation-schemas="calculationSchemas"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
                :root-calculation="props.calculation"
                :current-path="
                  props.currentPath
                    ? [...props.currentPath, configuredField.name]
                    : [configuredField.name]
                "
              />

              <!-- String Template -->
              <StringTemplateBuilder
                v-else-if="configuredField.field_type === CalculationFieldType.StringTemplate"
                :configuration-level="props.configurationLevel + 1"
                :available-columns="props.availableColumns"
                :delimiter_start="configuredField.field_type_args?.delimiter_start"
                :delimiter_end="configuredField.field_type_args?.delimiter_end"
                v-model="configuration[configuredField.name]"
                :place-holder="configuredField.placeholder"
                @update:modelValue="handleConfigurationChange"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
              />

              <!-- Json Type (Custom Config) -->
              <JsonCalculationConfiguration
                v-else-if="configuredField.field_type === CalculationFieldType.Json"
                :calculation-schemas="props.calculationSchemas"
                :available-columns="props.availableColumns"
                v-model="configuration"
                :editor-schema="
                  CalculationsJsonCalculationConfigurationService.getJsonEditorSchemaForCalculation()
                "
                :place-holder="configuredField.placeholder || 'Configure Json'"
                :configuration-level="props.configurationLevel"
                @calculation-type-changed="handleCalculationTypeChanged"
                @update:modelValue="handleConfigurationChange"
                :allow-type-change="true"
              />

              <!-- Default fallback for unknown field types -->
              <JsonCalculationConfiguration
                v-else
                :calculation-schemas="props.calculationSchemas"
                :available-columns="props.availableColumns"
                v-model="configuration[configuredField.name]"
                :editor-schema="
                  CalculationsJsonCalculationConfigurationService.getJsonEditorSchemaForField(
                    configuredField
                  )
                "
                :place-holder="configuredField.placeholder || 'Configure Json'"
                :configuration-level="props.configurationLevel"
                @update:modelValue="handleConfigurationChange"
                :allow-type-change="false"
              />
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>

  <div v-else class="flex h-full items-center justify-center text-gray-500">
    <div class="mt-8 text-center">
      <p class="mb-2 text-lg">No Calculation Selected</p>
      <p class="text-sm">Select a calculation from the left panel to configure its settings</p>
    </div>
  </div>
</template>

<style scoped>
  .calculation-configuration {
    padding: 10px;
  }

  .collapsed-calculation-configuration {
    display: none;
  }

  .horizontal-rule {
    border-bottom-color: #0000001f;
  }

  .calculation-form-fields-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: 1100px) {
    .calculation-form-fields-grid:not(.expanded) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .calculation-form-field-grid {
    width: 100%;
    min-height: 90px;
    max-height: 110px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow: hidden;
  }

  .array-calculation-builder-grid-span,
  .object-calculation-builder-grid-span,
  .json-calculation-configuration-grid-span,
  .single-field-grid-span {
    grid-column: 1 / -1 !important;
    min-height: unset;
    max-height: unset;
    overflow: visible;
  }

  .calculation-field-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
  }

  .calculation-field-description {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
  }

  .radio-icon-center {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 18px;
  }

  .checkbox-sm {
    font-size: 12px;
  }
  .checkbox-sm .ant-checkbox-inner {
    width: 14px;
    height: 14px;
  }
  .checkbox-md {
    font-size: 14px;
  }
  .checkbox-md .ant-checkbox-inner {
    width: 16px;
    height: 16px;
  }
  .checkbox-lg {
    font-size: 16px;
  }
  .checkbox-lg .ant-checkbox-inner {
    width: 20px;
    height: 20px;
  }
</style>
